import { ZOOM_DOMAIN_EDGE_PADDING } from '../../../../constants';
import type { DomainNormalizer, DomainNormalizerContext } from '../types';

/**
 * Domain normalizer for continuous scales (time/linear).
 *
 * Output format: `[number, number]` representing [min, max] values with padding
 *
 * ## Responsibilities
 *
 * 1. **Normalize zoom domains**: Convert any domain format to `[number, number]` with padding
 *    - Converts `[number, number]` and adds 1% padding
 *    - Converts `string[]` (from band scales) by parsing numeric values, snapping to data points, and adding padding
 *
 * 2. **Calculate domain from data**: When `zoomDomain` is null
 *    - Extracts min/max from chart data x-values
 *    - Adds 1% padding on each side for better drag-to-zoom UX
 *
 * ## Padding
 *
 * 1% padding is always added to the domain range (both zoomed and non-zoomed).
 * This creates extra interactive space near chart edges for drag-to-zoom.
 *
 * ## Cross-Chart Sync
 *
 * When a ColumnChart (band scale) zooms and sets a `string[]` domain like
 * `['1704067200000', '1704153600000', '1704240000000']`, this normalizer converts it
 * to a padded numeric domain for use in a synced LineChart.
 *
 * @example
 * const normalizer = new ContinuousScaleDomainNormalizer();
 *
 * // With zoom domain (adds padding)
 * normalizer.normalize({ zoomDomain: [100, 200], chartData, xDataKey: 'x' });
 * // Returns: [99, 201] (1% padding on range of 100)
 *
 * // Converting from band scale domain (snaps to data + adds padding)
 * normalizer.normalize({ zoomDomain: ['100', '150', '200'], chartData, xDataKey: 'x' });
 * // Returns: [99, 201] (snapped to data, then padded)
 *
 * // Without zoom domain (calculate from data with padding)
 * normalizer.normalize({ zoomDomain: null, chartData, xDataKey: 'x' });
 * // Returns: [96, 304] for data range 100-300 (1% padding = 2)
 */
export class ContinuousScaleDomainNormalizer implements DomainNormalizer {
  normalize(context: DomainNormalizerContext): [number, number] | null {
    const { zoomDomain, chartData, xDataKey } = context;

    // If zoomed, normalize the zoom domain to [number, number] with padding
    if (zoomDomain && Array.isArray(zoomDomain) && zoomDomain.length > 0) {
      return this.normalizeZoomDomain(zoomDomain, chartData, xDataKey);
    }

    // Not zoomed - calculate domain from data with padding
    return this.calculateDomainFromData(chartData, xDataKey);
  }

  /**
   * Adds padding to a domain based on its range.
   * Padding is skipped for single points or zero range.
   */
  private addPadding(domain: [number, number]): [number, number] {
    const [min, max] = domain;
    const range = max - min;

    // No padding for single point or zero range
    if (range === 0) {
      return domain;
    }

    const padding = range * ZOOM_DOMAIN_EDGE_PADDING;
    return [min - padding, max + padding];
  }

  /**
   * Normalizes a zoom domain to `[number, number]` format with padding.
   *
   * - `[number, number]` → adds padding
   * - `string[]` → parsed to numbers, snapped to actual data points if available, then adds padding
   *
   * @returns The normalized numeric domain with padding, or null if conversion fails
   */
  private normalizeZoomDomain(
    zoomDomain: [number, number] | string[],
    chartData: DomainNormalizerContext['chartData'],
    xDataKey: DomainNormalizerContext['xDataKey'],
  ): [number, number] | null {
    // Already in numeric format - add padding
    if (typeof zoomDomain[0] === 'number') {
      return this.addPadding(zoomDomain as [number, number]);
    }

    // Convert from string[] format (band scale domain)
    // Band scale domains are string representations of timestamps
    if (typeof zoomDomain[0] === 'string') {
      const stringDomain = zoomDomain as string[];

      // Parse first and last values to get the approximate range
      const first = Number(stringDomain[0]);
      const last = Number(stringDomain[stringDomain.length - 1]);

      if (Number.isNaN(first) || Number.isNaN(last)) {
        return null;
      }

      const approxMin = Math.min(first, last);
      const approxMax = Math.max(first, last);

      // If chartData is provided, snap to actual data point boundaries
      // This prevents x-axis tick duplication (same logic as ContinuousScaleDomainCalculator)
      if (chartData && xDataKey && chartData.length > 0) {
        // Collect and sort x values from chart data
        const xValues: number[] = [];
        for (const datum of chartData) {
          const xValue = datum[xDataKey];
          if (typeof xValue === 'number') {
            xValues.push(xValue);
          }
        }
        xValues.sort((a, b) => a - b);

        // Find the first data point >= approxMin and last data point <= approxMax
        let minDataPoint: number | null = null;
        let maxDataPoint: number | null = null;

        for (const xValue of xValues) {
          if (xValue >= approxMin && minDataPoint === null) {
            minDataPoint = xValue;
          }
          if (xValue <= approxMax) {
            maxDataPoint = xValue;
          } else {
            break; // Values are sorted, so we can stop
          }
        }

        // Return snapped domain with padding if we found valid data points
        if (minDataPoint !== null && maxDataPoint !== null && minDataPoint <= maxDataPoint) {
          return this.addPadding([minDataPoint, maxDataPoint]);
        }
      }

      // Fallback: return approximate range with padding (without snapping)
      return this.addPadding([approxMin, approxMax]);
    }

    return null;
  }

  /**
   * Calculates domain from chart data with padding for better drag-to-zoom interaction.
   *
   * Adds ZOOM_DOMAIN_EDGE_PADDING (1%) padding on each side of the data range.
   * This extra space makes it easier to start a drag-to-zoom near chart edges.
   *
   * @returns `[min - padding, max + padding]`, or null if no valid data
   */
  private calculateDomainFromData(
    chartData: DomainNormalizerContext['chartData'],
    xDataKey: DomainNormalizerContext['xDataKey'],
  ): [number, number] | null {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    // Extract numeric x values
    const xValues = chartData.map((d) => d[xDataKey]).filter((v): v is number => typeof v === 'number');

    if (xValues.length === 0) {
      return null;
    }

    const minValue = Math.min(...xValues);
    const maxValue = Math.max(...xValues);

    return this.addPadding([minValue, maxValue]);
  }
}
